/**
 * Fusion demo picker — same scenario-card chrome as the Workbench Demo library,
 * scoped to Fusion multi-surface walkthroughs.
 */
import { useMemo, useState } from 'react';
import { BookOpen, GraduationCap, X } from 'lucide-react';
import { DemoScenarioCard } from '../dashboard/DemoScenarioCard';
import { DashboardIcon } from '../dashboard/DashboardIcon';
import { splitDemoCatalog } from '../dashboard/demoCatalog';
import { listFusionDemos, type FusionDemo } from '../../data/fusionDemos';
import { getCategoryConfig } from '../dashboard/dashboardIconConfig';
import type { DemoCategory } from '../../data/demoGraphs';

type Props = {
  open: boolean;
  onClose: () => void;
  onSelect: (demo: FusionDemo) => void;
};

export function FusionDemoPicker({ open, onClose, onSelect }: Props) {
  const demos = useMemo(() => listFusionDemos(), []);
  const [showAll, setShowAll] = useState(false);

  if (!open) return null;

  const { visible, hiddenCount } = splitDemoCatalog(demos, showAll);

  const byCategory = (Object.keys(
    visible.reduce<Record<string, FusionDemo[]>>((acc, d) => {
      (acc[d.category] ||= []).push(d);
      return acc;
    }, {}),
  ) as DemoCategory[]).map((cat) => ({
    cat,
    meta: getCategoryConfig(cat),
    scenarios: visible.filter((d) => d.category === cat),
  }));

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center pt-[8vh] px-4 bg-black/40"
      onClick={onClose}
      data-testid="fusion-demo-picker"
    >
      <div
        className="w-full max-w-3xl max-h-[80vh] overflow-hidden rounded-xl border border-border bg-card shadow-2xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between gap-3 px-5 py-4 border-b border-border bg-brand-ink text-white shrink-0">
          <div className="flex items-center gap-2.5 min-w-0">
            <DashboardIcon variant="tile" tone="tutorials" icon={GraduationCap} />
            <div className="min-w-0">
              <h2 className="text-base font-semibold leading-tight flex items-center gap-2">
                <BookOpen className="w-4 h-4 text-primary shrink-0" />
                Fusion demo library
              </h2>
              <p className="text-xs text-white/70 truncate">
                Walkthroughs for Play, Court-safe, Run rail, and multi-surface joins
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-md hover:bg-white/10 text-white/70 hover:text-white transition-colors"
            aria-label="Close Fusion demo library"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-6">
          <p className="text-sm text-muted-foreground">
            Synthetic sandbox cases — not live investigations. Pick a scenario to load the canvas and open the Demo Guide.
          </p>

          {byCategory.map(({ cat, meta, scenarios }) => (
            <div key={cat}>
              <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                <DashboardIcon variant="section" tone={meta.tone} icon={meta.icon} />
                {meta.label}
                <span className="text-xs font-normal text-muted-foreground">({scenarios.length})</span>
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {scenarios.map((demo) => (
                  <DemoScenarioCard
                    key={demo.id}
                    scenario={{
                      id: demo.id,
                      title: demo.title,
                      description: demo.description,
                      category: demo.category,
                      tags: demo.tags,
                      priority: demo.priority,
                      startHereOrder: demo.startHereOrder,
                      demoType: demo.demoType,
                    }}
                    onClick={() => onSelect(demo)}
                  />
                ))}
              </div>
            </div>
          ))}

          {hiddenCount > 0 && (
            <button
              type="button"
              onClick={() => setShowAll((v) => !v)}
              className="text-sm font-medium text-primary hover:underline"
            >
              {showAll ? 'Show featured only' : `Show all ${demos.length} Fusion demos`}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
